import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";

import {
  IIntegrationQueueEntry,
  IntegrationQueueStatus,
} from "../../../@types/admin/AdminIntegrationQueue";
import { TableSorted } from "../../../components/TableSorted";
import { useIntegrationQueueEntries } from "../../../hooks/queries/admin/integrationQueue";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import { isDevOrStaging } from "../../../utils/environment";
import {
  AdminSelectField,
  ErrorMessage,
  FilterGroup,
  FilterLabel,
  FiltersContainer,
  PageContainer,
  PageDescription,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TableWrapper,
} from "../shared";
import { IntegrationMessagePayloadModal } from "./IntegrationMessagePayloadModal";

/** The two feeds the eMED integration delivers. */
const PROCESS_OPTIONS = [
  { value: "", label: "All processes" },
  { value: "EMED-LAB-RESULT", label: "Lab results" },
  { value: "EMED-MEDICATION", label: "Medications" },
];

/** Did the message parse? */
function parseTone(status: string): "active" | "error" | "inactive" {
  if (status === "Invalid" || status === "ExecutionFailed") return "error";
  if (status === "Valid") return "active";
  return "inactive";
}

/**
 * Did the queue deliver it? A falsy status means no queue row exists — the
 * field is ABSENT rather than null, because the API serialises with
 * `NullValueHandling.Ignore`, so this must not test `=== null`.
 */
function deliveryTone(
  status: IntegrationQueueStatus | null | undefined
): "active" | "error" | "warning" | "inactive" {
  if (!status) return "inactive";
  if (status === "Failed") return "error";
  if (status === "Sent") return "active";
  return "warning";
}

function deliveryLabel(
  status: IntegrationQueueStatus | null | undefined
): string {
  return status || "Not queued";
}

/**
 * How much of the message went out, when it produced more than one queue row.
 *
 * One inbound file can carry a row per patient, so "Failed" alone hides
 * whether one patient failed or all of them. Retool showed neither — it
 * joined file to queue rows and the operator read whichever row came first.
 */
function fanOutNote(entry: IIntegrationQueueEntry): string {
  if (entry.queueRowCount <= 1) return "";
  if (entry.queueFailedCount === 0) return `${entry.queueRowCount} rows`;
  return `${entry.queueFailedCount} of ${entry.queueRowCount} rows failed`;
}

/**
 * Integration queue inspector (EN-5174), replacing the READ half of Retool's
 * eMED Demo page: the 20 newest inbound integration messages, whether each
 * one parsed, and whether the queue delivered it. Click a row for the raw
 * HL7 payload.
 *
 * Two deliberate differences from Retool.
 *
 * 1. **Dev and staging only.** Retool pointed at a dedicated staging database
 *    resource, so the page showed staging data whatever environment the
 *    operator was in — it had no production meaning. Restricting the portal
 *    page to development and staging (agreed 2026-08-28) preserves that,
 *    while showing the operator's OWN environment rather than always staging.
 *    The API enforces it too: the endpoints 404 in UAT and production.
 *
 * 2. **No "send test message" action.** Retool's page could POST an HL7
 *    medication message to the integration API's `/Drugs/Json`. That is a
 *    PARTNER endpoint behind a partner JWT, and the Retool page carried
 *    hard-coded partner credentials in its request body. The pMp API holds an
 *    integration API key for the internal registration endpoint, not partner
 *    credentials, and giving it partner credentials so an admin page could
 *    inject messages is a security change well beyond migrating a demo tool.
 *    Injecting a test message stays a script in the integration repo — see
 *    retool-migration.md.
 */
export function AdminIntegrationQueue() {
  document.title = "Integration queue - Admin";

  const { openModal } = useModal();
  const [processName, setProcessName] = useState("");
  const { queueEntries, isLoading, isError, status } =
    useIntegrationQueueEntries(processName || undefined);

  const handleRowClick = (entry: IIntegrationQueueEntry) => {
    openModal(<IntegrationMessagePayloadModal entry={entry} />, {
      width: "min(1000px, 96vw)",
      maxHeight: "90vh",
      showCloseButton: true,
    });
  };

  const columns: ColumnDef<IIntegrationQueueEntry, string>[] = useMemo(
    () => [
      {
        accessorKey: "created",
        header: "Received",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created)}</span>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "processName",
        header: "Process",
        cell: ({ row }) => <span>{row.original.processName ?? "—"}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "status",
        header: "Parsed",
        cell: ({ row }) => (
          <StatusBadge status={parseTone(row.original.status)}>
            {row.original.status}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "queueStatus",
        header: "Delivered",
        // An accessorFn, not an accessorKey: TableSorted replaces the whole
        // cell with "-" when the accessor value is null, so a null
        // `queueStatus` would never reach the badge below. Accessing the
        // LABEL keeps the value non-empty, and "Not queued" is the state
        // worth naming — the message arrived and nothing has picked it up.
        accessorFn: (row) => deliveryLabel(row.queueStatus),
        cell: ({ row }) => {
          const note = fanOutNote(row.original);
          return (
            <StatusBadge status={deliveryTone(row.original.queueStatus)}>
              {note
                ? `${deliveryLabel(row.original.queueStatus)} · ${note}`
                : deliveryLabel(row.original.queueStatus)}
            </StatusBadge>
          );
        },
        enableSorting: true,
      },
      {
        accessorKey: "queueAttempts",
        header: "Attempts",
        // No cell override: TableSorted's own "-" is the right rendering for
        // a message that was never queued.
        enableSorting: true,
      },
      {
        id: "error",
        header: "Error",
        // Whichever half failed. A parse error means the queue never saw the
        // message at all, so it is the more informative of the two. Empty
        // when neither failed, which TableSorted renders as "-".
        accessorFn: (row) =>
          row.errorDescription ?? row.queueErrorDescription ?? "",
      },
      {
        accessorKey: "payloadLength",
        header: "Payload",
        // A fixed locale, not the host's: bare toLocaleString() groups with
        // "." under some ICU defaults, so the same row would read 1.234 for
        // one operator and 1,234 for another. en-GB is the app's fallback
        // language (config/i18n).
        cell: ({ row }) => (
          <span>{`${row.original.payloadLength.toLocaleString(
            "en-GB"
          )} chars`}</span>
        ),
        enableSorting: true,
      },
    ],
    []
  );

  // Belt and braces with the route gate: if the build is somehow served in
  // UAT or production, say so rather than showing an API failure.
  if (!isDevOrStaging()) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>Integration queue</PageTitle>
        </PageHeader>
        <ErrorMessage>
          The integration queue inspector is only available in development and
          staging.
        </ErrorMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>Integration queue</PageTitle>
          <PageInfoTooltip title="Integration queue">
            The 20 most recent inbound integration messages in this environment
            — whether each one parsed and whether the queue delivered it. One
            row per message: a batch carries a queue row per patient, and
            Delivered shows the worst state across them, so a partly-failed
            batch reads Failed. Click a row to read its raw payload. Development
            and staging only.
          </PageInfoTooltip>
        </PageTitleGroup>
      </PageHeader>

      <FiltersContainer>
        <FilterGroup>
          <FilterLabel htmlFor="integration-queue-process">Process</FilterLabel>
          <AdminSelectField
            id="integration-queue-process"
            value={processName}
            onChange={(event) => setProcessName(event.target.value)}
          >
            {PROCESS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>
      </FiltersContainer>

      {isError ? (
        <ErrorMessage>
          {status === 404
            ? "The integration queue inspector isn't available in this environment."
            : "Couldn't load the integration queue. Please try again."}
        </ErrorMessage>
      ) : null}

      {isLoading ? <TableLoadingState /> : null}

      {!isLoading && !isError ? (
        <TableContainer>
          <TableWrapper refreshKeys={[["admin-integration-queue"]]}>
            <TableSorted
              columns={columns}
              data={queueEntries}
              onRowClick={handleRowClick}
            />
          </TableWrapper>
        </TableContainer>
      ) : null}

      {!isLoading && !isError && queueEntries.length === 0 ? (
        <PageDescription>
          No integration messages with a stored payload in this environment.
        </PageDescription>
      ) : null}
    </PageContainer>
  );
}
